import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { CalendarCheck, Gem, Route, ScrollText, Swords, User, Waypoints, X } from "lucide-react";
import { TopBar } from "./components/shell/TopBar";
import { DuelHud } from "./components/arena/DuelHud";
import { QuoteTablet } from "./components/quote/QuoteTablet";
import { CardHand } from "./components/cards/CardHand";
import { ChallengeOverlay } from "./components/challenge/ChallengeOverlay";
import { VirtueRail } from "./components/deck/VirtueRail";
import { QuestPanel } from "./components/deck/QuestPanel";
import { ChapterMap } from "./components/deck/ChapterMap";
import { EvidencePanel } from "./components/evidence/EvidencePanel";
import { FxLayer } from "./components/fx/FxLayer";
import { VictoryBanner } from "./components/fx/VictoryBanner";
import { LossBanner } from "./components/fx/LossBanner";
import { TodayView } from "./components/views/TodayView";
import { PathView } from "./components/views/PathView";
import { MapView } from "./components/views/MapView";
import { MeView } from "./components/views/MeView";
import { useGame } from "./state/GameProvider";
import type { ViewId } from "./state/duelStore";
import { GameSurfaceIntegrated, GameSurfaceVisibility } from "./state/GameSurfaceVisibility";

type Drawer = "none" | "deck" | "evidence";

const NAV: Array<{ id: ViewId; label: string; icon: typeof CalendarCheck }> = [
  { id: "today", label: "今日", icon: CalendarCheck },
  { id: "path", label: "學程", icon: Route },
  { id: "duel", label: "對戰", icon: Swords },
  { id: "map", label: "圖譜", icon: Waypoints },
  { id: "me", label: "我", icon: User },
];

export default function App({ integrated = false, visible = true }: { integrated?: boolean; visible?: boolean }) {
  const { state, actions } = useGame();
  const [drawer, setDrawer] = useState<Drawer>("none");
  const panelDialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (!integrated) return;
    if (drawer !== "none" && visible) panelDialog.current?.showModal();
    else panelDialog.current?.close();
  }, [drawer, visible, integrated]);

  function toggle(target: Exclude<Drawer, "none">) {
    setDrawer((current) => (current === target ? "none" : target));
  }

  const inDuel = state.view === "duel";

  return (
    <GameSurfaceIntegrated.Provider value={integrated}>
    <GameSurfaceVisibility.Provider value={visible}>
    <div className={`app${integrated ? " integrated" : ""}${state.learnMode ? " learn" : ""} view-${state.view}`}>
      {!integrated && <TopBar />}

      {/* The arena is not wrapped in a motion element on purpose: swapping
          between two different element types inside AnimatePresence remounts
          the entering child and leaves its opacity animation stranded. Only
          the framing screens — all the same element type — are animated. */}
      {inDuel && (
        <div className="arena">
          {!integrated && <aside className={`rail left${drawer === "deck" ? " open" : ""}`} aria-label="牌組與章節">
            <QuestPanel />
            <VirtueRail />
            <ChapterMap onSelect={() => setDrawer("none")} />
          </aside>}

          <main className="stage">
            <DuelHud />
            <QuoteTablet />
            <CardHand />
          </main>

          {!integrated && <aside className={`rail right${drawer === "evidence" ? " open" : ""}`} aria-label="楊伯峻證據">
            <EvidencePanel />
          </aside>}
        </div>
      )}

      {/* Mount animation only — no exit. An AnimatePresence with mode="wait"
          deadlocks here if the student taps two tabs in quick succession: the
          first screen's exit is still running when the key changes again, so
          the incoming screen never mounts and the view appears frozen. */}
      {!inDuel && (
        <motion.main
          key={state.view}
          className="screen"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.22 }}
        >
          {state.view === "today" && <TodayView />}
          {state.view === "path" && <PathView />}
          {state.view === "map" && <MapView />}
          {state.view === "me" && <MeView />}
        </motion.main>
      )}

      {!integrated && <div className={`drawer-scrim${drawer !== "none" ? " show" : ""}`} onClick={() => setDrawer("none")} />}
      {integrated && <dialog ref={panelDialog} className={`workbench-game-panel ${drawer}`} aria-label={drawer === "deck" ? "牌組效果" : "本章譯注"} onCancel={() => setDrawer("none")} onClose={() => setDrawer("none")}>
        <div className="workbench-panel-heading"><h2>{drawer === "deck" ? "牌組效果" : "本章譯注"}</h2><button onClick={() => setDrawer("none")} aria-label="收起面板"><X size={20} /></button></div>
        {drawer === "deck" ? <VirtueRail /> : <EvidencePanel />}
      </dialog>}

      {inDuel && (
        <nav className="duelbar" aria-label="面板切換">
          <button className={drawer === "deck" ? "active" : ""} onClick={() => toggle("deck")}>
            <Gem size={15} />
            {integrated ? "牌組效果" : "牌組"}
          </button>
          <button className={drawer === "evidence" ? "active" : ""} onClick={() => toggle("evidence")}>
            <ScrollText size={15} />
            {integrated ? "本章譯注" : "證據"}
          </button>
        </nav>
      )}

      {!integrated && <nav className="tabbar" aria-label="主要導航">
        {NAV.map((item) => {
          const Icon = item.icon;
          const active = state.view === item.id;
          return (
            <button
              key={item.id}
              className={active ? "on" : ""}
              aria-current={active ? "page" : undefined}
              onClick={() => (item.id === "duel" ? actions.start() : actions.setView(item.id))}
            >
              <Icon size={19} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>}

      <div hidden={!visible}>
      <ChallengeOverlay />
      {!state.learnMode && inDuel && <FxLayer />}
      <VictoryBanner />
      <LossBanner />
      </div>
    </div>
    </GameSurfaceVisibility.Provider>
    </GameSurfaceIntegrated.Provider>
  );
}
